import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { useIntegratedFlowStore } from "../stores/useIntegratedFlowStore";
import { useProductOfferStore } from "../stores/useProductOfferStore";
import { SaleProgress } from "../types/types-sales";

export interface EnrollmentEventData {
    offerId: string;
    status: string;
    salesProvider: string;
}

/**
 * Handles an enrollment status event from the host's WebSocket.
 *
 * Usage in host (Workspace.tsx):
 *   import { handleEnrollmentEvent } from "@expert/sales-tools";
 *   expertWorkspaceWebSocketEventBus.on("ws_enrollment-event", (data) =>
 *       handleEnrollmentEvent(analytics, data)
 *   );
 */
export function handleEnrollmentEvent(analytics: SalesAnalytics, data: EnrollmentEventData): void {
    const { offerId, status, salesProvider } = data;
    const progress = convertToSaleProgress(status);
    if (!progress) return;

    const sessionId = useProductOfferStore.getState().getSessionIdForProductId(offerId);
    if (!sessionId) return;

    const primaryProducts = useProductOfferStore.getState().getPrimaryProducts(sessionId);
    if (!primaryProducts.some((p) => p.id === offerId)) return;

    useProductOfferStore.getState().updateProduct(sessionId, offerId, { saleProgress: progress });

    void analytics.dispatcher.dispatchBusinessEvent("EnrollmentEventReceived", {
        productOfferId: offerId,
        enrollmentStatus: status,
        sessionId,
        salesProvider,
    });

    analytics.trackFullStoryEvent(progress === SaleProgress.Success ? "EnrollmentSuccess" : "EnrollmentFailure", {
        productOfferId: offerId,
        sessionId,
        enrollmentStatus: status,
        statusOrigin: salesProvider,
    });

    if (useProductOfferStore.getState().isPrimaryProductInProgress(sessionId)) {
        useIntegratedFlowStore.getState().updateIntegratedStep(sessionId, "SuggestedOfferStep");
    }
}

function convertToSaleProgress(status: string): SaleProgress | undefined {
    switch (status) {
        case "SUCCESS":
        case "success":
            return SaleProgress.Success;
        case "FAILURE":
        case "failure":
            return SaleProgress.Failure;
        case "DECLINED":
        case "declined":
            return SaleProgress.Declined;
    }
}
